import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Table,
  MenuItem,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Typography,
  TableContainer,
} from "@mui/material";
import {
  ptBR,
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { format, isValid } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers";
import { EventNote, Visibility } from "@mui/icons-material";

import { useDebounce } from "../../shared/hooks";
import {
  IToken,
  IResponseToken,
  IParamsGetToken,
  AuditoriaService,
  IResponseTokenLog,
} from "../../shared/services/api/AuditoriaService";
import { Modal } from "../../shared/components/Modal";
import {
  IEstablishment,
  EstablishmentService,
} from "../../shared/services/api/EstablishmentService";
import { changeTitle, feedback } from "../../shared/events";
import { InputSearch } from "../../shared/components/InputSearch";
import { DrawerFilter } from "../../shared/components/DrawerFilter";

export const Auditoria: React.FC = () => {
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [countFilters, setCountFilters] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalLogOpen, setModalLogOpen] = useState(false);
  const [modalDetailsOpen, setModalDetailsOpen] = useState(false);
  const [establishments, setEstablishments] = useState<IEstablishment[]>([]);
  const [filters, setFilters] = useState<IParamsGetToken>({
    idEstabelecimento: 0,
  });
  const [responseToken, setResponseToken] = useState<IResponseToken>({
    data: [],
    qtdTotalDeRegistros: 0,
  });
  const [rowInfoClicked, setRowInfoClicked] = useState<IToken>({
    token: "",
    origem: "",
    nomeCompletoUsuario: "",
    contaEmailUsuario: "",
    cpfCnpjRneUsuario: "",
    ipUsuario: "",
    nomeEstabelecimento: "",
    dataGeracao: "",
    dataValidade: "",
    dataUso: "",
  });
  const [tokenDetailsLog, setTokenDetailsLog] = useState<IResponseTokenLog[]>(
    []
  );

  const { debounce } = useDebounce();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Ações",
        description: "Visualizar as informações detalhadas do token.",
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>
            <IconButton
              size="small"
              color="primary"
              onClick={() => (
                setRowInfoClicked(params.row), setModalDetailsOpen(true)
              )}
            >
              <Visibility fontSize="medium" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ ml: 0.5 }}
              color="primary"
              onClick={() => (
                setRowInfoClicked(params.row),
                handleGetTokenLog(params.row.token)
              )}
            >
              <EventNote fontSize="medium" />
            </IconButton>
          </>
        ),
      },
      {
        field: "token",
        headerName: "Token",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "dataGeracao",
        headerName: "Data de geração",
        disableColumnMenu: true,
        flex: 1,
        type: "dateTime",
        valueGetter: ({ value }) => value && new Date(value),
      },
      {
        field: "contaEmailUsuario",
        headerName: "E-mail",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "origem",
        headerName: "Origem",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "nomeCompletoUsuario",
        headerName: "Usuário",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "cpfCnpjRneUsuario",
        headerName: "CPF/CNPJ/RNE",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "ipUsuario",
        headerName: "IP",
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: "nomeEstabelecimento",
        headerName: "Estabelecimento",
        disableColumnMenu: true,
        flex: 1,
      },
    ],
    []
  );

  const handleGetTokens = useCallback(() => {
    debounce(async () => {
      setLoading(true);
      try {
        const response = await AuditoriaService.getTokens(filters);

        if (response.qtdTotalDeRegistros) {
          response.data = response.data.map((item, index) => ({
            id: index,
            ...item,
          }));

          setResponseToken(response);
        } else {
          setResponseToken(response);
          feedback("Sua pesquisa não possui resultados.", "warning");
        }
      } catch (error) {
        feedback(error.message, "error");
      } finally {
        setCountFilters(
          Object.values(filters)
            .map((item) => !!item)
            .filter(Boolean).length
        );
        setLoading(false);
        setDrawerOpen(false);
      }
    });
  }, [debounce, filters]);

  const handleGetTokenLog = useCallback(async (token: string) => {
    setLoading(true);
    try {
      const response = await AuditoriaService.getTokenLog(token);

      if (response.length) {
        setTokenDetailsLog(response);
        setModalLogOpen(true);
      } else {
        feedback("Não existem validações geradas para esse token.", "warning");
      }
    } catch (error) {
      feedback(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGetEstablishment = useCallback(async () => {
    setLoading(true);
    try {
      const response = await EstablishmentService.getEstablishments();

      if (response) {
        setEstablishments(response.estabelecimentos);
      }
    } catch (error) {
      console.warn(error);
      feedback(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleGetTokens();
  }, [filters.campoDePesquisa]);

  useEffect(() => {
    changeTitle("Auditoria");

    return () => changeTitle("");
  }, []);

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10} md={6}>
          <InputSearch
            disabled={loading}
            value={filters.campoDePesquisa}
            placeholder="Pesquisar por token, e-mail, origem..."
            onChange={({ target }) =>
              setFilters({ ...filters, campoDePesquisa: target.value })
            }
          />
        </Grid>

        <Grid item xs={2}>
          <DrawerFilter
            open={drawerOpen}
            loading={loading}
            filterDisabled={isError}
            countFilter={countFilters}
            handleFilter={() => handleGetTokens()}
            setCleanFilter={() => (
              setFilters({ idEstabelecimento: 0, campoDePesquisa: "" }),
              handleGetTokens()
            )}
            setOpen={() => (
              handleGetEstablishment(), setDrawerOpen(!drawerOpen)
            )}
          >
            <Grid container alignItems="center">
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Estabelecimento"
                  value={filters.idEstabelecimento}
                  onChange={({ target }) =>
                    setFilters({
                      ...filters,
                      idEstabelecimento: target.value,
                    })
                  }
                >
                  <MenuItem value={0}>Todos</MenuItem>
                  {establishments.map((item) => (
                    <MenuItem
                      key={item.idEstabelecimento}
                      value={item.idEstabelecimento}
                    >
                      {item.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                pt={3}
                pb={0}
                sx={{ color: "#777", fontSize: "0.875rem" }}
              >
                Período de geração
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  maxDate={new Date(filters.geracaoTerminaEm)}
                  value={
                    filters.geracaoIniciaEm
                      ? format(
                          new Date(filters.geracaoIniciaEm),
                          "yyyy-MM-dd'T'00:00:01"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      geracaoIniciaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'00:00:01"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={1}
                sx={{
                  color: "#777",
                  padding: "8px 5px 12px !important",
                  fontSize: "0.875rem",
                }}
              >
                até
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  minDate={new Date(filters.geracaoIniciaEm)}
                  value={
                    filters.geracaoTerminaEm
                      ? format(
                          new Date(filters.geracaoTerminaEm),
                          "yyyy-MM-dd'T'23:59:59"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      geracaoTerminaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'23:59:59"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                pt={3}
                pb={0}
                sx={{ color: "#777", fontSize: "0.875rem" }}
              >
                Período de validade
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  maxDate={new Date(filters.validadeTerminaEm)}
                  value={
                    filters.validadeIniciaEm
                      ? format(
                          new Date(filters.validadeIniciaEm),
                          "yyyy-MM-dd'T'00:00:01"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      validadeIniciaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'00:00:01"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={1}
                sx={{
                  color: "#777",
                  padding: "8px 5px 12px !important",
                  fontSize: "0.875rem",
                }}
              >
                até
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  minDate={new Date(filters.validadeIniciaEm)}
                  value={
                    filters.validadeTerminaEm
                      ? format(
                          new Date(filters.validadeTerminaEm),
                          "yyyy-MM-dd'T'23:59:59"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      validadeTerminaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'23:59:59"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                pt={3}
                pb={0}
                sx={{ color: "#777", fontSize: "0.875rem" }}
              >
                Período de uso
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  maxDate={new Date(filters.usoTerminaEm)}
                  value={
                    filters.usoIniciaEm
                      ? format(
                          new Date(filters.usoIniciaEm),
                          "yyyy-MM-dd'T'00:00:01"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      usoIniciaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'00:00:01"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={1}
                sx={{
                  color: "#777",
                  padding: "8px 5px 12px !important",
                  fontSize: "0.875rem",
                }}
              >
                até
              </Grid>
              <Grid item xs>
                <DatePicker
                  onError={(error) => setIsError(!!error)}
                  minDate={new Date(filters.geracaoTerminaEm)}
                  value={
                    filters.usoTerminaEm
                      ? format(
                          new Date(filters.usoTerminaEm),
                          "yyyy-MM-dd'T'23:59:59"
                        )
                      : null
                  }
                  onChange={(newValue: Date) =>
                    setFilters({
                      ...filters,
                      usoTerminaEm:
                        isValid(newValue) &&
                        format(newValue, "yyyy-MM-dd'T'23:59:59"),
                    })
                  }
                  renderInput={(params) => (
                    <TextField size="small" {...params} />
                  )}
                />
              </Grid>
            </Grid>
          </DrawerFilter>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Box component={Paper}>
            <DataGrid
              loading={loading}
              rows={responseToken.data}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(size) => setPageSize(size)}
              rowsPerPageOptions={[10, 15, 20, 50]}
              disableSelectionOnClick
              autoHeight
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{
                sorting: {
                  sortModel: [{ field: "dataGeracao", sort: "desc" }],
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Modal visualização dos detalhes do Token */}
      <Modal
        maxWidth="md"
        loading={loading}
        opened={modalDetailsOpen}
        labelCloseButton="Fechar"
        onClose={() => setModalDetailsOpen(false)}
        title="Detalhes"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom component="div">
              <strong>Dados do usuário</strong>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Nome"
              value={rowInfoClicked.nomeCompletoUsuario}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="CPF/CNPJ/RNE"
              value={rowInfoClicked.cpfCnpjRneUsuario}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="IP"
              value={rowInfoClicked.ipUsuario}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Origem"
              value={rowInfoClicked.origem}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Estabelecimento"
              value={rowInfoClicked.nomeEstabelecimento}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom component="div">
              <strong>Dados do token</strong>
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Código"
              value={rowInfoClicked.token}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="E-mail de destino"
              value={rowInfoClicked.contaEmailUsuario}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Data de geração"
              value={
                rowInfoClicked.dataGeracao
                  ? format(
                      new Date(rowInfoClicked.dataGeracao),
                      "dd/MM/yyyy' às 'HH:mm"
                    )
                  : "-"
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Data de validade"
              value={
                rowInfoClicked.dataValidade
                  ? format(
                      new Date(rowInfoClicked.dataValidade),
                      "dd/MM/yyyy' às 'HH:mm"
                    )
                  : "-"
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Data de uso"
              value={
                rowInfoClicked.dataUso
                  ? format(
                      new Date(rowInfoClicked.dataUso),
                      "dd/MM/yyyy' às 'HH:mm"
                    )
                  : "-"
              }
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Modal visualização do Log do Token */}
      <Modal
        maxWidth="lg"
        loading={loading}
        opened={modalLogOpen}
        labelCloseButton="Fechar"
        onClose={() => setModalLogOpen(false)}
        title="Dados do Token "
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Código"
              value={rowInfoClicked.token}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Tipo"
              value={rowInfoClicked.origem}
            />
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead sx={{ background: "#E0E0E0" }}>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokenDetailsLog.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {new Date(row.dataGeracao).toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell>{row.idUsuario}</TableCell>
                      <TableCell>{row.mensagem}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};
